import React, { useRef, useEffect } from 'react';
import { Terminal, Bell } from 'lucide-react';

// ==================== LED GAUGE COMPONENT ====================
export const LEDGauge = ({ label, value, unit, maxValue = 100, thresholds = { warning: 50, danger: 80 }, size = 'normal' }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const numBars = 20;
  const activeBars = Math.round((percentage / 100) * numBars);
  
  const getBarColor = (index) => {
    const barPercentage = ((index + 1) / numBars) * 100;
    if (barPercentage <= 40) return '#10b981'; // Green
    if (barPercentage <= 70) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  const getValueColor = () => {
    if (percentage >= thresholds.danger) return '#ef4444';
    if (percentage >= thresholds.warning) return '#f59e0b';
    return '#10b981';
  };

  const fontSize = size === 'large' ? 'text-6xl' : size === 'small' ? 'text-4xl' : 'text-5xl';
  const labelSize = size === 'large' ? 'text-xl' : size === 'small' ? 'text-base' : 'text-lg';

  return (
    <div className="bg-black border-2 border-marine-slate/60 p-6 relative">
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-5 h-5 border-l-3 border-t-3 border-marine-cyan" />
      <div className="absolute top-0 right-0 w-5 h-5 border-r-3 border-t-3 border-marine-cyan" />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-l-3 border-b-3 border-marine-cyan" />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-r-3 border-b-3 border-marine-cyan" />
      
      <p className={`font-mono text-marine-cyan ${labelSize} uppercase tracking-wider mb-4 text-center font-bold`}>{label}</p>
      
      {/* LED Value Display */}
      <div className="text-center mb-5">
        <span 
          className={`font-heading ${fontSize} font-bold tracking-wider`}
          style={{ 
            color: getValueColor(),
            textShadow: `0 0 15px ${getValueColor()}, 0 0 30px ${getValueColor()}`,
            fontFamily: "'Share Tech Mono', monospace"
          }}
        >
          {typeof value === 'number' ? value.toFixed(1) : value}
        </span>
        <span className="text-2xl text-white/90 ml-2">{unit}</span>
      </div>
      
      {/* LED Bar Display */}
      <div className="flex gap-1 justify-center">
        {Array.from({ length: numBars }).map((_, idx) => (
          <div
            key={idx}
            className="w-3 h-10 rounded-sm transition-all duration-300"
            style={{
              backgroundColor: idx < activeBars ? getBarColor(idx) : '#1e293b',
              boxShadow: idx < activeBars ? `0 0 10px ${getBarColor(idx)}` : 'none',
              opacity: idx < activeBars ? 1 : 0.3
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ==================== HORIZONTAL LED BAR ====================
export const LEDBar = ({ label, currentValue, crisisValue, unit, color = 'cyan' }) => {
  const colors = {
    cyan: { bg: '#22d3ee', glow: 'rgba(34, 211, 238, 0.5)' },
    red: { bg: '#ef4444', glow: 'rgba(239, 68, 68, 0.5)' },
    amber: { bg: '#fbbf24', glow: 'rgba(251, 191, 36, 0.5)' },
    green: { bg: '#10b981', glow: 'rgba(16, 185, 129, 0.5)' },
  };

  const c = colors[color] || colors.cyan;
  const increase = ((crisisValue - currentValue) / currentValue * 100).toFixed(0);

  return (
    <div className="bg-black border border-marine-slate/40 p-4">
      <p className="font-mono text-marine-cyan text-sm uppercase tracking-wider mb-3">{label}</p>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex justify-between mb-2">
            <span className="font-mono text-white/80 text-xs">ACTUEL</span>
            <span 
              className="font-heading text-2xl font-bold"
              style={{ color: '#10b981', textShadow: '0 0 10px rgba(16, 185, 129, 0.5)' }}
            >
              {currentValue.toFixed(0)}{unit}
            </span>
          </div>
          <div className="h-3 bg-marine-slate/30 flex gap-0.5">
            {Array.from({ length: 30 }).map((_, i) => (
              <div 
                key={i} 
                className="flex-1 h-full"
                style={{ 
                  backgroundColor: i < 15 ? '#10b981' : '#1e293b',
                  boxShadow: i < 15 ? '0 0 4px rgba(16, 185, 129, 0.5)' : 'none'
                }}
              />
            ))}
          </div>
        </div>
        <div className="text-center px-4">
          <span className="font-heading text-3xl text-marine-red font-bold" style={{ textShadow: '0 0 10px rgba(239, 68, 68, 0.5)' }}>
            +{increase}%
          </span>
        </div>
        <div className="flex-1">
          <div className="flex justify-between mb-2">
            <span className="font-mono text-white/80 text-xs">CRISE</span>
            <span 
              className="font-heading text-2xl font-bold"
              style={{ color: '#ef4444', textShadow: '0 0 10px rgba(239, 68, 68, 0.5)' }}
            >
              {crisisValue.toFixed(0)}{unit}
            </span>
          </div>
          <div className="h-3 bg-marine-slate/30 flex gap-0.5">
            {Array.from({ length: 30 }).map((_, i) => (
              <div 
                key={i} 
                className="flex-1 h-full"
                style={{ 
                  backgroundColor: '#ef4444',
                  boxShadow: '0 0 4px rgba(239, 68, 68, 0.5)'
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ALERT BANNER ====================
export const AlertBanner = ({ type, message, action, onAction }) => {
  const styles = {
    warning: 'bg-marine-amber/30 border-marine-amber text-marine-amber',
    success: 'bg-marine-green/30 border-marine-green text-marine-green',
    danger: 'bg-marine-red/30 border-marine-red text-marine-red',
    info: 'bg-marine-cyan/30 border-marine-cyan text-marine-cyan',
  };

  return (
    <div className={`p-4 border-l-4 ${styles[type]} flex items-center justify-between`}>
      <div className="flex items-center gap-3">
        <Bell className="w-6 h-6 animate-pulse" />
        <span className="font-mono text-base font-bold text-white">{message}</span>
      </div>
      {action && (
        <button onClick={onAction} className="px-6 py-2 bg-white/10 border border-current font-mono text-sm uppercase tracking-wider hover:bg-white/20">
          {action}
        </button>
      )}
    </div>
  );
};

// ==================== VERBOSE LOG ====================
export const VerboseLog = ({ logs, isRunning }) => {
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to bottom when logs change
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const getLogClass = (status) => {
    switch (status) {
      case 'success': return 'text-marine-green';
      case 'error': return 'text-marine-red';
      case 'warning': return 'text-marine-amber';
      default: return 'text-marine-cyan';
    }
  };

  return (
    <div className="bg-black/80 border-2 border-marine-slate/50 h-72">
      <div className="bg-marine-slate/30 px-4 py-3 flex items-center gap-2 border-b border-marine-slate/50">
        <Terminal className="w-6 h-6 text-marine-cyan" />
        <span className="font-mono text-lg text-marine-cyan uppercase tracking-wider font-bold">Console Verbose</span>
        {isRunning && (
          <span className="ml-auto flex items-center gap-2 text-marine-green text-lg font-bold">
            <span className="w-4 h-4 bg-marine-green rounded-full animate-pulse" />
            SCAN ACTIF
          </span>
        )}
      </div>
      {/* Use native div with overflow for reliable auto-scroll */}
      <div 
        ref={scrollContainerRef}
        className="h-56 p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-marine-slate scrollbar-track-transparent"
        style={{ scrollBehavior: 'smooth' }}
      >
        {logs.map((log, idx) => (
          <div key={idx} className={`font-mono text-lg py-2 border-b border-marine-slate/10 ${getLogClass(log.status)}`}>
            <span className="text-white/80">[{new Date(log.timestamp).toLocaleTimeString('fr-FR')}]</span>
            {' '}<span className="text-marine-amber font-bold">{log.source}</span>
            {' '}<span className="text-white">{log.message}</span>
          </div>
        ))}
        {logs.length === 0 && (
          <p className="text-white/90 text-lg font-mono">En attente du scan...</p>
        )}
      </div>
    </div>
  );
};
